import { Divider, Stack, Typography } from "@mui/material";
import { useSessionContext } from "../../../contexts/SessionContext";
import { CardRow, EditableCardRow } from "../../../components/common";
import { useFormatter } from "../../../utils/useFormatter";
import { ExitToApp, Google } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { DeviceIDPatch } from "@/services/scim/profile/DeviceIDPatch";
import { GoogleRevokeDialog } from "../../fluxextensions/components/GoogleRevokeDialog";
import { SocialNameDialog } from "./SocialNameDialog/SocialNameDialog";
import { useForm } from "react-hook-form";

const SCOPES = [
  "https://www.googleapis.com/auth/calendar.events", // Permite ver, editar e criar eventos
  "https://www.googleapis.com/auth/userinfo.email", // Permite obter o email do usuário
];

declare global {
  interface Window {
    gapi: any;
    tokenClient: any;
    google: any;
    googlePopupTimeout: NodeJS.Timeout;
  }
}

interface CardInfoProps {
  id?: string;
}

export function CardInfo({ id }: CardInfoProps) {
  const { user, userPatient, fetchUser, setAccessTokenGoogle, access } =
    useSessionContext();
  const { formatAge } = useFormatter;
  const [isLoading, setIsLoading] = useState(false);
  const [openRevokeModal, setOpenRevokeModal] = useState(false);
  const [dialogSocialName, setDialogSocialName] = useState(false);

  const auxSocialName = userPatient?.name?.find(
    (el: any) => el.use === "usual" && el?.text
  )?.text;

  const { control, handleSubmit } = useForm({
    defaultValues: {
      socialName: auxSocialName,
    },
  });

  useEffect(() => {
    const loadGoogleScripts = () => {
      // Carrega o script do Google Identity Services
      const gsiScript = document.createElement("script");
      gsiScript.src = "https://accounts.google.com/gsi/client";
      gsiScript.async = true;
      gsiScript.defer = true;
      gsiScript.onload = () => {
        console.log("GSI carregado, inicializando cliente...");
        initializeGoogleClient();
      };
      document.head.appendChild(gsiScript);

      // Carrega o script do GAPI
      const gapiScript = document.createElement("script");
      gapiScript.src = "https://apis.google.com/js/api.js";
      gapiScript.async = true;
      gapiScript.defer = true;
      document.head.appendChild(gapiScript);
    };

    // Adiciona um listener para mensagens de erro ou cancelamento do Google
    const handleGoogleMessage = (event: MessageEvent) => {
      // Verifica se a mensagem vem do Google e se indica cancelamento
      if (
        event.origin.includes("accounts.google.com") &&
        event.data &&
        (event.data.type === "cancel" || event.data.type === "close")
      ) {
        setIsLoading(false);
        console.log("Autenticação do Google cancelada pelo usuário");
      }
    };

    window.addEventListener("message", handleGoogleMessage);

    const initializeGoogleClient = () => {
      if (!window.google) return;

      // @ts-ignore
      window.tokenClient = window.google.accounts.oauth2.initCodeClient({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        scope: SCOPES.join(" "),
        callback: (response: any) => {
          if (window.googlePopupTimeout) {
            clearTimeout(window.googlePopupTimeout);
          }
          handleTokenResponse(response);
        },
        access_type: "offline",
        prompt: "consent",
        include_granted_scopes: true,
        select_account: false,
        ux_mode: "popup",
      });

      console.log("Cliente Google inicializado com sucesso");
    };

    loadGoogleScripts();

    return () => {
      const scripts = document.head.getElementsByTagName("script");
      Array.from(scripts).forEach((script) => {
        if (
          script.src.includes("accounts.google.com") ||
          script.src.includes("apis.google.com")
        ) {
          script.remove();
        }
      });

      // Remove o listener quando o componente é desmontado
      window.removeEventListener("message", handleGoogleMessage);
    };
  }, []);

  const handleTokenResponse = async (response: any) => {
    // console.log("Resposta inicial recebida:", response);

    try {
      if (response.code) {
        // Troca o código por tokens
        const tokenResponse = await fetch(
          "https://oauth2.googleapis.com/token",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams({
              client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
              client_secret: import.meta.env.VITE_GOOGLE_CLIENT_SECRET,
              code: response.code,
              grant_type: "authorization_code",
              access_type: "offline",
              redirect_uri: window.location.origin,
            }),
          }
        );

        if (!tokenResponse.ok) {
          const errorData = await tokenResponse.json();
          console.error("Erro ao trocar código por tokens:", errorData);
          throw new Error("Falha ao obter tokens");
        }

        const tokens = await tokenResponse.json();
        // console.log("Tokens recebidos:", {
        //   access_token: tokens.access_token
        //     ? tokens.access_token
        //     : "Não recebido",
        //   refresh_token: tokens.refresh_token
        //     ? tokens.refresh_token
        //     : "Não recebido",
        //   expires_in: tokens.expires_in,
        // });

        if (tokens.refresh_token) {
          // console.log("Refresh token recebido:", tokens.refresh_token);

          // Add refresh token do user
          await DeviceIDPatch({
            idUser: user?.id,
            operation: "replace",
            pathName:
              "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:google_refresh_token",
            valueField: tokens.refresh_token,
          });

          setAccessTokenGoogle(tokens.access_token);

          // Armazena o token no localStorage
          localStorage.setItem("googleAccessToken", tokens.access_token);

          fetchUser();
        } else {
          console.warn(
            "Refresh token não recebido. Tente remover o acesso do app e tentar novamente."
          );
        }
      }
    } catch (error) {
      console.error("Erro ao processar resposta:", error);
      alert("Erro ao fazer login. Por favor, tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!window.tokenClient) {
      console.error("Cliente do Google não está inicializado");
      return;
    }

    try {
      setIsLoading(true);
      console.log("Iniciando processo de login...");

      // Configura um timeout para resetar o estado de loading caso o callback não seja chamado
      const popupTimeout = setTimeout(() => {
        setIsLoading(false);
        console.log(
          "Timeout de autenticação - possivelmente o popup foi fechado"
        );
      }, 60000); // 1 minuto de timeout

      // Armazena o timeout para que possa ser limpo no callback
      window.googlePopupTimeout = popupTimeout;

      // Solicita código de autorização
      window.tokenClient.requestCode();
    } catch (error) {
      console.error("Erro ao solicitar autorização:", error);
      setIsLoading(false);
      alert("Erro ao fazer login. Por favor, tente novamente.");
    }
  };

  const handleGoogleLogout = async () => {
    try {
      setIsLoading(true);
      console.log("Iniciando logout...");

      // Remove refresh token do user
      await DeviceIDPatch({
        idUser: user?.id,
        operation: "replace",
        pathName:
          "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:google_refresh_token",
        valueField: "",
      });

      // const accessToken = localStorage.getItem("googleAccessToken");
      // if (accessToken) {
      //   // Revoga o token
      //   await fetch(
      //     `https://oauth2.googleapis.com/revoke?token=${accessToken}`,
      //     {
      //       method: "POST",
      //       headers: {
      //         "Content-Type": "application/x-www-form-urlencoded",
      //       },
      //     }
      //   );

      // Limpa os tokens do localStorage
      // localStorage.removeItem("googleAccessToken");

      // console.log("Tokens removidos com sucesso");
      // Exibe modal para o usuário revogar permissões manualmente
      setOpenRevokeModal(true);
      // } else {
      //   setOpenRevokeModal(true);
      // }
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler para fechar o modal e atualizar o usuário
  const handleCloseRevokeModal = () => {
    setOpenRevokeModal(false);
    fetchUser();
  };

  // Handler para abrir a página de permissões do Google
  const handleOpenPermissionsPage = () => {
    window.open("https://myaccount.google.com/permissions", "_blank");
  };

  const handleGoogleAuth = () => {
    if (user.refreshTokenGoogle) {
      handleGoogleLogout();
    } else {
      handleGoogleLogin();
    }
  };

  return (
    <>
      <Stack
        id={id}
        sx={{
          border: "1px solid",
          borderColor: "neutral700.main",
          borderRadius: "15px",
        }}
        width="100%"
        paddingTop={2}
      >
        <Typography
          variant="h5"
          fontWeight="500"
          paddingBottom={3}
          paddingLeft={5}
        >
          Informações básicas
        </Typography>
        <CardRow
          title="Nome"
          titleContext={useFormatter.formatNameDisplay(user.name)}
        />

        <Divider sx={{ marginLeft: 5 }} />

        <EditableCardRow
          type={!auxSocialName ? "add" : "edit"}
          title="Nome social"
          titleContext={useFormatter.formatNameDisplay(auxSocialName) || "-"}
          onClick={() => setDialogSocialName(true)}
        />

        <Divider sx={{ marginLeft: 5 }} />
        <CardRow
          title="CPF"
          titleContext={useFormatter.formatCPF(user.username)}
        />
        <Divider sx={{ marginLeft: 5 }} />
        <CardRow
          title="Data de nascimento"
          titleContext={
            useFormatter.formatDate(user.birthDate) +
            " - " +
            formatAge(user.birthDate) +
            " anos"
          }
        />
        <Divider sx={{ marginLeft: 5 }} />
        <CardRow
          title="Sexo biológico"
          titleContext={useFormatter.formatGender(userPatient?.gender)}
        />
        {access?.type === "professional" && (
          <>
            <Divider sx={{ marginLeft: 5 }} />
            <EditableCardRow
              title="Conectar com Google Agenda"
              titleContext={`${
                user.refreshTokenGoogle ? "Conectado" : "Desconectado"
              }`}
              onClick={handleGoogleAuth}
              auxText={
                isLoading
                  ? "Carregando..."
                  : user.refreshTokenGoogle
                  ? "Desvincular conta google"
                  : "Vincular conta google"
              }
              auxIcon={user.refreshTokenGoogle ? <ExitToApp /> : <Google />}
              colorButton={
                user.refreshTokenGoogle ? "error700.main" : "primary700.main"
              }
            />
          </>
        )}

        {/* Modal para revogar permissões do Google */}
        <GoogleRevokeDialog
          isOpen={openRevokeModal}
          onClose={handleCloseRevokeModal}
          onOpenPermissionsPage={handleOpenPermissionsPage}
        />
      </Stack>

      {dialogSocialName && (
        <SocialNameDialog
          type={!auxSocialName ? "add" : "edit"}
          isOpen={dialogSocialName}
          handleClose={() => setDialogSocialName(false)}
          display={useFormatter.formatNameDisplay(auxSocialName)}
          control={control}
          handleSubmit={handleSubmit}
        />
      )}
    </>
  );
}
